import React, { useEffect } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import dayjs from "dayjs";
import { Carousel } from "react-responsive-carousel";
import {
  useAuthState,
  requestListBanner,
  requestListNews,
  requestListTutoringProgramSchedule,
  requestListWebinar,
  useBannerDispatch,
  useBannerState,
  useNewsDispatch,
  useNewsState,
  useTutoringProgramDispatch,
  useTutoringProgramState,
  useWebinarDispatch,
  useWebinarState,
} from "../../Context";
import "./index.css";
import {
  ChartSquareBarIcon,
  ChatAlt2Icon,
  CreditCardIcon,
  DocumentTextIcon,
  ReceiptTaxIcon,
  SwitchHorizontalIcon,
  TagIcon,
  AcademicCapIcon,
  ClipboardListIcon,
} from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";
import { ConsultationFormWidget } from "../../Components/ConsultationFormWidget";
import { ConsultantSettingsWidget } from "../../Components/ConsultantSettingsWidget";
import { OngoingConsultationList } from "../../Components/OngoingConsultationList";
import TutoringScheduleCard from "../../Components/TutoringScheduleCard";
import WebinarScheduleCard from "../../Components/WebinarScheduleCard";
import NewsCard from "../../Components/NewsCard";
import NewsCardSkeleton from "../../Components/NewsCardSkeleton";
import { CalculatorIcon } from "@heroicons/react/outline";
import { getSafeNavigationUrl } from "../../Utils/SafeUrlUtils";
import {
  contentfulImage,
  contentfulPictureSources,
  contentfulSrcSet,
} from "../../Utils/ImageUtils";

// the content column tops out at 1216px (max-w-screen-xl minus lg:px-8)
const BANNER_WIDTHS = [640, 960, 1216, 1600, 1920];
const BANNER_ASPECT_RATIO = 4 / 1;
const BANNER_SIZES =
  "(min-width: 1280px) 1216px, (min-width: 1024px) calc(100vw - 64px), (min-width: 640px) calc(100vw - 48px), calc(100vw - 32px)";

const menus = [
  {
    title: "NPWP",
    icon: <CreditCardIcon />,
    href: "/npwp-services",
  },
  {
    title: "Consultation",
    icon: <ChatAlt2Icon />,
    href: "/consultation?status=ALL",
  },
  {
    title: "E-Billing",
    icon: <ReceiptTaxIcon />,
    href: "/ebilling",
  },
  {
    title: "E-Meterai",
    icon: <DocumentTextIcon />,
    href: "/emeterai",
  },
  {
    title: "Learn",
    icon: <AcademicCapIcon />,
    href: "/learn",
  },
  {
    title: "Licensing",
    icon: <ClipboardListIcon />,
    href: "/business-license",
  },
  {
    title: "Accounting",
    icon: <ChartSquareBarIcon />,
    href: "/v2/accounting",
  },
  {
    title: "Cash Book",
    icon: <SwitchHorizontalIcon />,
    href: "/v2/cash-book",
  },
  {
    title: "taxCalculator.title",
    icon: <CalculatorIcon />,
    href: "/tax-calculator",
  },
  {
    title: "Subscription Plan",
    icon: <TagIcon />,
    href: "/subscription",
  },
];

function Dashboard() {
  const { t } = useTranslation();

  const { user } = useAuthState();
  const isConsultant = user?.membership === "CONSULTANT";

  const dispatch = useBannerDispatch();
  const { loading: bannerLoading, error, banners } = useBannerState();
  const tutoringDispatch = useTutoringProgramDispatch();
  const { tutoringList } = useTutoringProgramState();
  const webinarDispatch = useWebinarDispatch();
  const { webinars } = useWebinarState();
  const newsDispatch = useNewsDispatch();
  const { news, loadingNews } = useNewsState();

  useEffect(() => {
    const currentDate = new Date();
    const oneHourAfterNow = new Date(
      currentDate.getTime() + 1 * 60 * 60 * 1000,
    );
    const threeDaysAfterNow = new Date(
      currentDate.getTime() + 3 * 24 * 60 * 60 * 1000,
    );
    const sevenDaysAfterNow = new Date(
      currentDate.getTime() + 7 * 24 * 60 * 60 * 1000,
    );

    requestListTutoringProgramSchedule(tutoringDispatch, {
      page: 1,
      startOfDay: oneHourAfterNow.toISOString(),
      endOfDay: threeDaysAfterNow.toISOString(),
    });

    requestListWebinar(webinarDispatch, {
      page: 1,
      startOfDay: dayjs(currentDate).startOf("day").toISOString(),
      endOfDay: sevenDaysAfterNow.toISOString(),
      includeAllTypes: true,
    });
  }, [tutoringDispatch, webinarDispatch]);

  useEffect(() => {
    requestListBanner(dispatch);
    requestListNews(newsDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterAndSortUniquePrograms = (tutoringList) => {
    const uniqueProgramIDs = new Set();
    const sortedUniquePrograms = tutoringList
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
      .filter((item) => {
        if (!uniqueProgramIDs.has(item.programID)) {
          uniqueProgramIDs.add(item.programID);
          return true;
        }
        return false;
      });

    return sortedUniquePrograms.slice(0, 3);
  };

  const filterAndSortUniqueWebinars = (webinarList) => {
    const now = new Date();
    const uniqueWebinarIDs = new Set();

    const filteredWebinars = webinarList.filter((webinar) => {
      const webinarStart = new Date(webinar.scheduledAt);
      const webinarEnd = new Date(
        webinarStart.getTime() + webinar.duration * 60000,
      );

      const daysUntilWebinar = Math.ceil(
        (webinarStart - now) / (1000 * 60 * 60 * 24),
      );

      const isWithinTimeRange =
        webinar.type === "PAID" ? daysUntilWebinar <= 7 : daysUntilWebinar <= 3;

      if (
        webinarEnd > now &&
        !uniqueWebinarIDs.has(webinar.id) &&
        isWithinTimeRange
      ) {
        uniqueWebinarIDs.add(webinar.id);
        return true;
      }
      return false;
    });

    const sortedUniqueWebinars = filteredWebinars
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
      .slice(0, 2);

    return sortedUniqueWebinars;
  };

  return (
    <div>
      <div className="mt-4 sm:mt-8">
        {bannerLoading ? (
          <div className="animate-pulse w-full aspect-[4/1] bg-gray-300 rounded-md sm:rounded-lg"></div>
        ) : (
          <Carousel
            className="hover:cursor-pointer"
            autoPlay
            infiniteLoop
            interval={5000}
            showThumbs={false}
            showStatus={false}
            onClickItem={(index, item) => {
              // Banner targets are stored content, so the href is validated
              // again here rather than trusted from the rendered element.
              const safeUrl = getSafeNavigationUrl(item.props.href, {
                allowAnyHttpsHost: true,
              });
              if (safeUrl) {
                window.location.href = safeUrl;
              }
            }}
          >
            {banners.map((banner, index) => {
              // banner.url is already resolved by the banner action (MENU
              // targets go through pathBuilder, URL targets stay as-is).
              const safeUrl = getSafeNavigationUrl(banner.url, {
                allowAnyHttpsHost: true,
              });
              const sources = contentfulPictureSources(
                banner.imageUrl,
                BANNER_WIDTHS,
                { aspectRatio: BANNER_ASPECT_RATIO }
              );
              // every slide stays in the DOM, so only the visible one is worth
              // fetching up front
              const isFirstSlide = index === 0;

              return (
                <a
                  href={safeUrl || "#"}
                  key={banner.id}
                  className="block w-full aspect-[4/1] overflow-hidden rounded-md sm:rounded-lg"
                >
                  <picture className="block w-full h-full">
                    {sources.map(({ type, srcSet }) => (
                      <source
                        key={type}
                        type={type}
                        srcSet={srcSet}
                        sizes={BANNER_SIZES}
                      />
                    ))}
                    <img
                      src={contentfulImage(banner.imageUrl, {
                        width: 1216,
                        height: 304,
                        format: null,
                      })}
                      srcSet={contentfulSrcSet(banner.imageUrl, BANNER_WIDTHS, {
                        aspectRatio: BANNER_ASPECT_RATIO,
                        format: null,
                      })}
                      sizes={BANNER_SIZES}
                      width={1216}
                      height={304}
                      loading={isFirstSlide ? "eager" : "lazy"}
                      fetchpriority={isFirstSlide ? "high" : undefined}
                      className="w-full h-full aspect-[4/1] object-cover object-center rounded-md sm:rounded-lg"
                      alt="banner"
                    />
                  </picture>
                </a>
              );
            })}
          </Carousel>
        )}
        {error && (
          <div className="w-full aspect-[4/1] bg-gray-300 rounded-md sm:rounded-lg flex items-center justify-center">
            <p className="mx-auto text-sm">
              {t(error.message) || `${t("Error fetching banner.")}`}
            </p>
          </div>
        )}
      </div>

      {isConsultant && <OngoingConsultationList />}

      <div className="grid grid-cols-1 lg:grid-cols-3 mt-4 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200/60 shadow-sm divide-y divide-slate-100">
          <div className="flex flex-col p-6">
            <h3
              data-testid="dashboard-menu"
              className="mb-4 font-semibold text-lg text-slate-900 tracking-tight hidden lg:block"
            >
              {t("Services")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {menus.map((menu) => {
                const testId = `select-menu-${menu.title
                  .toLowerCase()
                  .replace(/ /g, "-")}`;
                return (
                  <a
                    href={menu.href}
                    key={menu.title}
                    data-testid={testId}
                    className="group relative flex flex-col items-center justify-center text-center p-4 bg-white border border-slate-200/60 rounded-lg hover:border-blue-600/20 hover:shadow-md hover:shadow-blue-600/10 transition-all duration-200 ease-in-out hover:-translate-y-0.5 aspect-square"
                  >
                    <div className="flex items-center justify-center w-12 h-12 mb-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 group-hover:from-blue-600 group-hover:to-blue-700 transition-all duration-200">
                      {React.cloneElement(menu.icon, {
                        className:
                          "h-6 w-6 text-blue-600 group-hover:text-white transition-colors duration-200",
                      })}
                    </div>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 transition-colors duration-200 leading-tight">
                      {t(menu.title)}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
          {filterAndSortUniqueWebinars(webinars).length > 0 && (
            <div className="flex flex-col p-6">
              <h3 className="mb-4 font-semibold text-lg text-slate-900 tracking-tight">
                {t("Webinar")}
              </h3>
              <WebinarScheduleCard
                items={filterAndSortUniqueWebinars(webinars)}
              />
            </div>
          )}
          {tutoringList?.length > 0 && (
            <div className="flex flex-col p-6">
              <h3 className="mb-4 font-semibold text-lg text-slate-900 tracking-tight">
                {t("Tutoring Programs")}
              </h3>
              <TutoringScheduleCard
                items={filterAndSortUniquePrograms(tutoringList)}
              />
            </div>
          )}
        </div>

        <div className="p-6 bg-white rounded-xl border border-slate-200/60 shadow-sm">
          <h3 className="mb-4 font-semibold text-lg text-slate-900 tracking-tight">
            {t(isConsultant ? "Consultant Settings" : "Instant Tax Consultation")}
          </h3>
          <div className="bg-white border border-slate-200/60 rounded-lg overflow-hidden">
            {isConsultant ? <ConsultantSettingsWidget /> : <ConsultationFormWidget />}
          </div>
        </div>
      </div>

      {(loadingNews || news.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm mt-4">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-slate-900 tracking-tight">
                {t("Latest Tax News")}
              </h3>
              <a
                href="https://blog.pajakind.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200"
              >
                {t("View All")} →
              </a>
            </div>
            <div className="block md:hidden">
              <div className="grid grid-cols-1 gap-4">
                {loadingNews
                  ? Array.from({ length: 4 }, (_, index) => (
                      <NewsCardSkeleton key={index} />
                    ))
                  : news.map((news) => <NewsCard key={news.id} news={news} />)}
              </div>
            </div>

            <div className="hidden md:block overflow-x-auto">
              <div className="flex gap-4 pb-2" style={{ width: "max-content" }}>
                {loadingNews
                  ? Array.from({ length: 4 }, (_, index) => (
                      <div key={index} className="flex-shrink-0 w-80">
                        <NewsCardSkeleton />
                      </div>
                    ))
                  : news.map((news) => (
                      <div key={news.id} className="flex-shrink-0 w-80">
                        <NewsCard news={news} />
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
